import axios, { AxiosRequestConfig } from 'axios';
import { ENVS, isSessionExpiredError } from '../../../http';
import { store } from '../../../store';

const env = process.env.REACT_APP_ENV || 'dev';

// The shared api client has /v1 baked into baseURL. Build a parallel /v2 client.
// ENVS is always defined in the app; guard only so this module can still load
// under tests that mock `../../../http` without re-exporting ENVS (the real
// base is unused there — HTTP is mocked).
const baseV1 = ENVS?.[env]?.BASE_URL ?? 'http://localhost:3333/v1'; // e.g. https://aware-api-dev.pom-bright.com/v1
const baseV2 = baseV1.replace(/\/v1\/?$/, '/v2');

export const apiV2 = axios.create({ baseURL: baseV2 });

// Mirror the shared client's request interceptor — attach the auth token from
// the redux store on every request. Without this every request fails with
// MISSING_TOKEN.
apiV2.interceptors.request.use((config: AxiosRequestConfig) => {
  const headers: Record<string, string> = {
    authorization: `${store.getState().auth.token}`,
  };
  if (env === 'ngrok') {
    headers['ngrok-skip-browser-warning'] = 'true';
  }
  return { ...config, headers } as AxiosRequestConfig;
});

// Mirror the shared client's response interceptor — surface 401 / 403 as
// the same window events the rest of the app already listens to.
apiV2.interceptors.response.use(
  (r) => r,
  (error: any) => {
    if (isSessionExpiredError(error)) {
      window.dispatchEvent(new Event('sessionExpired'));
    }
    if (error?.response?.status === 403) {
      window.dispatchEvent(new Event('permissionsChanged'));
    }
    return Promise.reject(error);
  },
);

export type SettingsLevel = 'system' | 'customer' | 'group' | 'team' | 'user';
export type PolicyMode = 'open' | 'locked' | 'locked-subtree' | 'constrained';
// Levels this editor can write at — system defaults + user prefs are edited
// elsewhere. Shared here so helpers can import it without a circular dep.
export type EditableLevel = Extract<SettingsLevel, 'customer' | 'group' | 'team'>;

export interface CatalogEntry {
  type: 'boolean' | 'integer' | 'number' | 'string' | 'enum' | 'object' | 'stringArray';
  default?: unknown;
  enum?: string[];
  min?: number;
  max?: number;
  itemRegex?: string;
  allowedLevels: SettingsLevel[];
  lockable: boolean;
  legacyAliases: string[];
  description: string;
  pendingReview?: boolean;
  migrationNote?: string;
}

export interface CatalogResponse {
  schemaVersion: number;
  settings: Record<string, CatalogEntry>;
}

export interface ResolvedSetting {
  value: unknown;
  source: SettingsLevel;
  canOverride: boolean;
  // The resolver code emits any of customer | group | team | user (level can
  // be 'user' on the last loop iteration). The server-side type currently
  // narrows this incorrectly — see pom-2-api src/settings/types.ts.
  lockedBy?: Exclude<SettingsLevel, 'system'>;
  reason?: string;
}

export interface EffectiveChainEntry {
  id: string;
  // 'provided' = ID was passed in the query; 'derived' = server walked it up
  // from the deepest provided entity (user→team→group→customer).
  source: 'provided' | 'derived';
}

export interface EffectiveResponse {
  environment: string;
  // Optional: servers older than the chain-derivation change omit it.
  chain?: Partial<Record<Exclude<SettingsLevel, 'system'>, EffectiveChainEntry>>;
  resolved: Record<string, ResolvedSetting>;
}

export interface OrgTreeTeam { id: string; name: string }
export interface OrgTreeGroup { id: string; name: string; teams: OrgTreeTeam[] }
export interface CustomerOrgTreeResponse {
  customer: { id: string; name: string };
  groups: OrgTreeGroup[];
  teamsWithoutGroup: OrgTreeTeam[];
}

export const v2SettingsApi = {
  async getCatalog(): Promise<CatalogResponse> {
    const r = await apiV2.get<CatalogResponse>('/settings/catalog');
    return r.data;
  },

  async getEffective(params: { customerId?: string; groupId?: string; teamId?: string; userId?: string }): Promise<EffectiveResponse> {
    const r = await apiV2.get<EffectiveResponse>('/settings/effective', { params });
    return r.data;
  },

  // Self-scoped effective settings for the CURRENT web-app user's own org.
  // Unlike getEffective, requires no ids and no POM-master role — every
  // authenticated web-app user can read their own org's resolved settings.
  async getMine(): Promise<EffectiveResponse> {
    const r = await apiV2.get<EffectiveResponse>('/settings/me');
    return r.data;
  },

  async getCustomerOrgTree(customerId: string): Promise<CustomerOrgTreeResponse> {
    const r = await apiV2.get<CustomerOrgTreeResponse>('/settings/customer-org-tree', { params: { customerId } });
    return r.data;
  },

  async patchSystemDefault(canonicalName: string, value: unknown): Promise<void> {
    await apiV2.patch('/settings/system', { canonicalName, value });
  },

  async patchEntityOverride(
    level: 'customer' | 'group' | 'team' | 'user',
    id: string,
    canonicalName: string,
    body: { value?: unknown; policy?: { mode: PolicyMode; reason?: string } },
  ): Promise<void> {
    await apiV2.patch(`/settings/${level}/${id}`, { canonicalName, ...body });
  },

  async deleteEntityOverride(
    level: 'customer' | 'group' | 'team' | 'user',
    id: string,
    canonicalName: string,
    scope?: 'override' | 'policy' | 'both',
  ): Promise<void> {
    await apiV2.delete(`/settings/${level}/${id}`, {
      data: { canonicalName, ...(scope ? { scope } : {}) },
    });
  },
};
